import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './apiClient';
import { listThreads } from './mailApi';
import type { MailFolder, MailThreadSummaryDto } from './mailTypes';

const messageOf = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

/** Cursor-paged thread list for one mailbox + folder, with local patch/remove helpers. */
export function useMailThreads(mailboxId: string | null, folder: MailFolder) {
  const [threads, setThreads] = useState<MailThreadSummaryDto[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    setThreads([]);
    setNextCursor(undefined);
    setError(null);
    if (!mailboxId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listThreads(mailboxId, folder)
      .then((page) => {
        if (cancelled) return;
        setThreads(page.threads);
        setNextCursor(page.nextCursor);
      })
      .catch((err) => !cancelled && setError(messageOf(err, 'Could not load messages')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [mailboxId, folder, reloadToken]);

  const loadMore = useCallback(() => {
    if (!mailboxId || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    listThreads(mailboxId, folder, { cursor: nextCursor })
      .then((page) => {
        setThreads((prev) => {
          const seen = new Set(prev.map((t) => t.id));
          return [...prev, ...page.threads.filter((t) => !seen.has(t.id))];
        });
        setNextCursor(page.nextCursor);
      })
      .catch((err) => setError(messageOf(err, 'Could not load more messages')))
      .finally(() => setLoadingMore(false));
  }, [mailboxId, folder, nextCursor, loadingMore]);

  const patchThread = useCallback(
    (summary: MailThreadSummaryDto) =>
      setThreads((prev) => prev.map((t) => (t.id === summary.id ? summary : t))),
    [],
  );
  const removeThread = useCallback(
    (id: string) => setThreads((prev) => prev.filter((t) => t.id !== id)),
    [],
  );

  return {
    threads,
    hasMore: !!nextCursor,
    loading,
    loadingMore,
    error,
    loadMore,
    reload: () => setReloadToken((t) => t + 1),
    patchThread,
    removeThread,
  };
}
